import React, { useCallback, useEffect, useState } from "react";
import { Swords, Loader2, Play, RefreshCw, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { SlideStrat } from "./floor/SlideStrat";

const GOLD = "#F5C451";
const usd = (v, d = 0) => `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}`;
const signed = (v) => `${v < 0 ? "-" : "+"}${usd(Math.abs(v), 2)}`;
const pc = (v) => (v >= 0 ? "text-emerald-300" : "text-rose-300");
const ct = (iso) => { try { return new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/Chicago", hour: "numeric", minute: "2-digit" }); } catch (e) { return "--"; } };
const px = (v) => (v == null ? "--" : v >= 100 ? v.toFixed(2) : v.toPrecision(5));

const Stat = ({ label, value, sub, cls = "text-white", testid }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid={testid}>
    <p className="nx-mono text-[10px] tracking-[0.25em] text-amber-100/50">{label}</p>
    <p className={`nx-orbitron text-2xl font-black mt-1 ${cls}`}>{value}</p>
    {sub && <p className="nx-mono text-[11px] text-white/40 mt-1">{sub}</p>}
  </div>
);

const CapitalBar = ({ deployed, min, max }) => {
  const pct = Math.min(100, (deployed / max) * 100);
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="hercules-capital-bar">
      <div className="flex justify-between nx-mono text-[11px] text-amber-100/60 mb-2">
        <span>CAPITAL DEPLOYED</span><b className="text-white" data-testid="hercules-deployed">{usd(deployed)}</b>
      </div>
      <div className="relative h-4 rounded-full bg-white/5 overflow-hidden">
        <div className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-700" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${GOLD}, #FF7A3D)` }} />
        <div className="absolute inset-y-0 w-px bg-white/70" style={{ left: `${(min / max) * 100}%` }} />
      </div>
      <div className="flex justify-between nx-mono text-[10px] text-white/40 mt-1.5">
        <span>$0</span><span style={{ marginLeft: `${(min / max) * 100 - 8}%` }}>min {usd(min)}</span><span>max {usd(max)}</span>
      </div>
    </div>
  );
};

const TradesTable = ({ trades }) => (
  <div className="rounded-2xl border border-white/10 overflow-x-auto" data-testid="hercules-trades-table">
    <table className="w-full nx-mono text-[12px]">
      <thead className="text-amber-100/50 text-[10px] tracking-widest bg-white/[0.03]">
        <tr>{["TIME", "SYMBOL", "SIDE", "AGENT", "SIGNAL", "SIZE", "ENTRY", "CURRENT", "P&L"].map((h) => <th key={h} className="text-left px-3 py-2 font-normal">{h}</th>)}</tr>
      </thead>
      <tbody>
        {trades.map((t) => (<React.Fragment key={t.id}>
          <tr className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`hercules-trade-${t.id}`} title={t.why}>
            <td className="px-3 py-2 text-white/50">{ct(t.at)}</td>
            <td className="px-3 py-2 text-white font-bold">{t.symbol}{t.status !== "open" && <span className="ml-1 text-[9px] text-white/40" data-testid={`hercules-trade-status-${t.id}`}>{t.status === "pending" ? "awaiting fill" : t.status}</span>}</td>
            <td className={`px-3 py-2 ${t.side === "long" ? "text-emerald-300" : "text-rose-300"}`}>{t.side.toUpperCase()}</td>
            <td className="px-3 py-2 text-amber-100/80">{t.agent}</td>
            <td className="px-3 py-2 text-cyan-200/70">{t.signal}</td>
            <td className="px-3 py-2">{usd(t.usd)}</td>
            <td className="px-3 py-2 text-white/70">{t.entry == null ? "--" : px(t.entry)}</td>
            <td className="px-3 py-2 text-white/70">{px(t.current)}</td>
            <td className={`px-3 py-2 font-bold ${pc(t.pnl || 0)}`}>{signed(t.pnl || 0)}</td>
          </tr>
          {(t.research || []).length > 0 && (
            <tr data-testid={`hercules-trade-research-${t.id}`}><td colSpan={9} className="px-3 pb-2">
              <div className="space-y-0.5 border-l-2 border-amber-300/30 pl-3">
                {t.research.map((b, i) => (
                  <p key={i} className="text-[11px] text-white/60 leading-snug">{b.text} <span className="text-white/35">· {b.as_of}</span>{" "}
                    <a href={b.source_url} target="_blank" rel="noreferrer" className="text-amber-200 hover:underline">SEC source</a></p>
                ))}
              </div>
            </td></tr>
          )}
        </React.Fragment>))}
        {!trades.length && <tr><td colSpan={9} className="px-3 py-6 text-center text-white/40">No trades yet today. Hercules 2.0 plans and deploys at the open (8:35 CT).</td></tr>}
      </tbody>
    </table>
  </div>
);

const Sources = ({ list }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="hercules-sources">
    <p className="nx-mono text-[10px] tracking-[0.25em] text-amber-100/50 mb-2">SIGNAL SOURCES</p>
    <div className="space-y-1.5">
      {list.map((s) => (
        <p key={s.id} className="flex items-center gap-2 text-[12px]" data-testid={`hercules-source-${s.id}`}>
          {s.status === "connected" ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-300 shrink-0" /> : <XCircle className="w-3.5 h-3.5 text-white/30 shrink-0" />}
          <span className={s.status === "connected" ? "text-white/80" : "text-white/40"}>{s.name}</span>
          {s.status !== "connected" && <span className="nx-mono text-[10px] text-white/40 ml-auto">{s.status}</span>}
        </p>
      ))}
    </div>
  </div>
);

const History = ({ rows }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="hercules-history">
    <p className="nx-mono text-[10px] tracking-[0.25em] text-amber-100/50 mb-2">DAILY HISTORY</p>
    {!rows.length && <p className="text-white/40 text-[12px]">First day on the floor.</p>}
    {rows.map((h) => (
      <div key={h.day} className="grid grid-cols-4 nx-mono text-[12px] py-1 border-t border-white/5">
        <span className="text-white/60">{h.day}</span><span>{usd(h.deployed)}</span>
        <span className={pc(h.pnl)}>{signed(h.pnl)}</span><span className={h.target_hit ? "text-emerald-300" : "text-white/40"}>{h.target_hit ? "target hit" : "missed"}</span>
      </div>
    ))}
  </div>
);

const Steps = ({ steps, schedule }) => (
  <div className="flex flex-wrap gap-2" data-testid="hercules-steps">
    {Object.entries(schedule).map(([k, at]) => (
      <span key={k} title={steps[k]?.note || ""} className={`px-3 py-1 rounded-full nx-mono text-[10px] tracking-widest border ${steps[k] ? "border-amber-300/50 text-amber-200" : "border-white/10 text-white/40"}`}>
        {k.toUpperCase()} {at} CT {steps[k] ? "· done" : ""}
      </span>
    ))}
  </div>
);

export default function HerculesFloor() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/hercules/floor").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [load]);
  const run = async () => {
    setBusy(true);
    try { const r = await api.post("/hercules/run", { step: "auto" }, { timeout: 300000 }); if (r.data.ok) { toast(`Hercules 2.0 is running ${r.data.step} — trades appear in ~2 min`); setTimeout(load, 120000); } else toast(r.data.reason); }
    catch (e) { toast.error("Hercules 2.0 run failed"); } finally { setBusy(false); }
  };
  if (!d) return <div className="h-full flex items-center justify-center text-amber-200/60" data-testid="hercules-loading"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const t = d.today, note = Object.values(t.steps).slice(-1)[0]?.note;
  return (
    <div className="h-full overflow-y-auto bg-[#0a0604] text-white px-4 sm:px-8 py-6 space-y-5" data-testid="hercules-floor">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: `radial-gradient(circle at 50% 30%, ${GOLD}, #3a1a05 75%)`, boxShadow: `0 0 28px ${GOLD}66` }}><Swords className="w-6 h-6 text-white" /></div>
          <div>
            <h1 className="nx-orbitron text-3xl sm:text-4xl font-black tracking-[0.15em]" style={{ color: GOLD }}>HERCULES 2.0</h1>
            <p className="text-sm text-amber-100/60">Hercules 2.0 + Mr Strat + all 8 AV Trading v3 agents · fund goals: {d.fund.goals.monthly_goal_pct}% a month · {d.fund.goals.annual_goal_pct}% a year · max {d.fund.goals.max_drawdown_pct}% drawdown · stocks on Tradier sandbox, crypto on Alpaca paper</p>
            {!d.broker_ready && <p className="text-xs text-rose-300 mt-1" data-testid="hercules-broker-missing">Broker keys not configured — Hercules 2.0 can't place trades yet.</p>}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="hercules-refresh" className="h-10 w-10 rounded-full border border-white/10 flex items-center justify-center text-white/60 hover:text-white transition-colors"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={run} disabled={busy} data-testid="hercules-run" className="h-10 px-5 rounded-full font-bold text-[#1a0d02] flex items-center gap-2 transition-transform hover:scale-[1.03] disabled:opacity-60" style={{ background: GOLD }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run next step
          </button>
        </div>
      </div>
      <Steps steps={t.steps} schedule={d.rules.schedule_ct} />
      {note && <p className="text-[13px] text-amber-50/80 border-l-2 pl-3 italic" style={{ borderColor: GOLD }} data-testid="hercules-note">"{note}" — Hercules 2.0</p>}
      <CapitalBar deployed={t.deployed} min={d.rules.min_capital} max={d.rules.max_capital} />
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="hercules-target-math">
        <Stat testid="hercules-total-pnl" label="TOTAL P&L" value={signed(t.pnl)} cls={pc(t.pnl)} sub={`${t.return_pct}% on deployed`} />
        <Stat testid="hercules-equity" label="EQUITY" value={usd(d.fund.equity, 2)} sub={`overall ${signed(d.fund.overall.pnl)} · ${d.fund.overall.return_pct}%`} cls="text-amber-200" />
        <Stat testid="hercules-monthly" label="MONTH TO DATE" value={signed(d.fund.month.pnl)} sub={`${d.fund.month.progress_pct}% of ${usd(d.fund.month.goal_usd)} goal`} />
        <Stat testid="hercules-annual" label="YEAR TO DATE" value={`${d.fund.year.return_pct}%`} sub={`${d.fund.year.progress_pct}% of ${d.fund.year.goal_pct}% goal`} />
        <Stat testid="hercules-drawdown" label="DRAWDOWN" value={`${d.fund.drawdown.pct}%`} sub={d.fund.drawdown.guard_tripped ? "guard tripped — entries paused" : `guard ${d.fund.drawdown.limit_pct}% from high-water`} cls={d.fund.drawdown.guard_tripped ? "text-rose-300" : ""} />
      </div>
      <TradesTable trades={t.trades} />
      <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 h-[420px] flex flex-col" data-testid="hercules-strat">
        <p className="nx-mono text-[10px] tracking-[0.25em] text-amber-100/50 mb-2">MR STRAT · HEAD OF STRATEGY · HERCULES 2.0</p>
        <div className="flex-1 min-h-0"><SlideStrat /></div>
      </div>
      <div className="grid lg:grid-cols-2 gap-3"><Sources list={d.sources} /><History rows={d.history} /></div>
    </div>
  );
}
